import React from "react";
import { View, StyleSheet } from "react-native";
import { colors, layout, shadows } from "@/constants";
import Interactive from "./Interactive";
import Text from "./Text";

/**
 * Props for the Pill component
 */
interface PillProps {
  /** The text to display inside the pill */
  label: string;
  /** Whether the pill is currently selected */
  isSelected?: boolean;
  /** Callback when the pill is pressed (optional - if not provided, pill is display-only) */
  onPress?: () => void;
  /** Accessibility label for screen readers */
  accessibilityLabel?: string;
}

/**
 * A reusable pill-shaped component for tags, categories, or selectable options.
 * Can be used as an interactive button (with onPress) or display-only (without onPress).
 * Provides visual feedback for selection state with consistent styling.
 *
 * @param props - The component props
 * @returns A pill component - interactive if onPress is provided, display-only otherwise
 *
 * @example
 * ```tsx
 * // Interactive pill
 * <Pill
 *   label="Bug"
 *   isSelected={selectedTags.includes("bug")}
 *   onPress={() => toggleTag("bug")}
 * />
 *
 * // Display-only pill (for summaries, read-only views)
 * <Pill
 *   label="Theme"
 *   accessibilityLabel="Theme: Theme name"
 * />
 *
 * // With accessibility
 * <Pill
 *   label="Suggestion"
 *   isSelected={isSelected}
 *   onPress={handleSelect}
 *   accessibilityLabel="Suggestion feedback type"
 * />
 * ```
 */
export default function Pill({
  label,
  isSelected = false,
  onPress,
  accessibilityLabel
}: PillProps) {
  const containerStyle = [styles.pill, isSelected && styles.pillSelected];
  const textStyle = [styles.pillText, isSelected && styles.pillTextSelected];

  if (onPress) {
    return (
      <Interactive
        style={containerStyle}
        onPress={onPress}
        accessibilityLabel={accessibilityLabel || `${label} option`}
        accessibilityState={{ selected: isSelected }}
      >
        <Text size="medium" style={textStyle}>
          {label}
        </Text>
      </Interactive>
    );
  }

  // Display-only version
  return (
    <View
      style={containerStyle}
      accessibilityLabel={accessibilityLabel || label}
      accessibilityRole="text"
    >
      <Text size="medium" style={textStyle}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    paddingHorizontal: layout.space.md,
    paddingVertical: layout.space.sm,
    borderRadius: layout.borderRadius.round,
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.outline,
    ...shadows.subtle
  },
  pillSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary
  },
  pillText: {
    color: colors.onSurface
  },
  pillTextSelected: {
    color: colors.onPrimary
  }
});
